/**
 * Module 'comments': a discussion thread on a backlog item or a mission. A comment is Markdown
 * (rendered without raw HTML) and may carry attachments (module 'attachments', target
 * { kind: 'comment', id }). Browser-safe and pure.
 */
import type { Id, Ts } from '../index.ts';

export const COMMENTS_EXT = 'comments';

export type CommentTargetKind = 'item' | 'mission';
export const COMMENT_TARGET_KINDS: readonly CommentTargetKind[] = ['item', 'mission'];

export interface CommentTarget {
  kind: CommentTargetKind;
  id: Id;
}

export interface Comment {
  id: Id;
  target: CommentTarget;
  /** Markdown. Attachments it shows inline are referenced as attachment:<id>. */
  body: string;
  by: string;
  createdAt: Ts;
  editedAt: Ts | null;
  /** Attachments on this comment (their target is { kind: 'comment', id }). */
  attachmentIds: Id[];
}

/** state.ext.comments */
export interface CommentsState {
  comments: Comment[];
}

/** POST /api/comments */
export interface CommentInput {
  target: CommentTarget;
  body: string;
  /** Uploaded attachments (drafts of the caller) to attach to the new comment. */
  attachmentIds?: Id[];
}

/** PATCH /api/comments/:id */
export interface CommentPatch {
  body?: string;
  attachmentIds?: Id[];
}

/** What other hub modules may ask: ctx.ext<CommentsApi>('comments'). */
export interface CommentsApi {
  comment(id: Id): Comment | undefined;
  on(target: CommentTarget): Comment[];
}

export const COMMENT_MAX = 20_000;
export const COMMENTS_PER_TARGET = 1_000;

export const COMMENTS_AUDIT_LABEL: Record<string, string> = {
  'comment.create': 'Add comment',
  'comment.update': 'Edit comment',
  'comment.delete': 'Delete comment',
};

export function commentTargetOf(v: unknown): CommentTarget | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { kind?: unknown; id?: unknown };
  if (!COMMENT_TARGET_KINDS.includes(o.kind as CommentTargetKind)) return null;
  if (typeof o.id !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(o.id)) return null;
  return { kind: o.kind as CommentTargetKind, id: o.id };
}

/** A plain-language problem with a comment body, or null when it is fine. */
export function commentInputError(v: unknown, partial = false): string | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Send the comment as a JSON object.';
  const o = v as Record<string, unknown>;
  if (!partial && !commentTargetOf(o.target)) return 'Say which item or mission the comment is on.';
  const ids = o.attachmentIds;
  if (ids !== undefined && (!Array.isArray(ids) || ids.length > 50 || ids.some((x) => typeof x !== 'string'))) return 'Attachments must be a list of at most 50 attachment ids.';
  const hasFiles = Array.isArray(ids) && ids.length > 0;
  if (!partial || 'body' in o) {
    if (typeof o.body !== 'string') return 'Write the comment as text.';
    if (!o.body.trim() && !hasFiles) return 'Write something, or attach a file.';
    if (o.body.length > COMMENT_MAX) return `Keep the comment under ${COMMENT_MAX} characters.`;
  }
  return null;
}
